/**
 * Parses a preference's regex into a fixed set of dropdown options, when the regex is a
 * simple enumeration (e.g. "^LIGHT|DARK$") rather than a general pattern (e.g. a hex-color
 * character class like "^[0-9a-fA-F]+$"). Returns null when the regex doesn't represent an
 * enumeration, so the caller falls back to a free-text field.
 *
 * Rule: strip a leading ^ and trailing $ if present, split the remainder on "|", and treat it
 * as an enum only if that yields 2+ tokens that are each plain literals (letters/digits/
 * underscore only -- no regex metacharacters). Anything else (character classes, quantifiers,
 * groups, a single alternative) is not treated as an enum.
 *
 * Options are always lowercased: actual stored preference values are lowercase in practice
 * (e.g. user_preferences has "light"/"dark"), regardless of the letter case the preference's
 * own regex happens to be authored in (e.g. "^LIGHT|DARK$"). The regex is only used here to
 * discover the set of valid tokens, never to dictate their case.
 */
export function parseRegexOptions(regex: string): string[] | null {
    if (!regex) {
        return null;
    }
    let pattern = regex;
    if (pattern.startsWith('^')) {
        pattern = pattern.substring(1);
    }
    if (pattern.endsWith('$')) {
        pattern = pattern.substring(0, pattern.length - 1);
    }
    const tokens = pattern.split('|');
    if (tokens.length < 2) {
        return null;
    }
    const isPlainLiteral = (token: string) => /^[A-Za-z0-9_]+$/.test(token);
    if (!tokens.every(isPlainLiteral)) {
        return null;
    }
    return tokens.map(token => token.toLowerCase());
}

/**
 * Derives the default value for a preference that has no stored override, from its regex:
 * the first option when the regex is an enumeration (see parseRegexOptions), otherwise an
 * empty string -- "false" doesn't make sense as a default for most of these preferences (e.g.
 * a hex basemap ID), only for genuinely boolean ones (handled separately by datatype).
 */
export function deriveDefaultValue(regex: string): string {
    const options = parseRegexOptions(regex);
    return options ? options[0] : '';
}

export class OrgDefaultUserPreference {
    name: string;
    displayName: string;
    description: string;
    regex: string;
    value: any;
    datatype: string;
    entitlementName: string;
    entitlementDisplayName: string;
    hasOverride: boolean;
    dropdownOptions: string[] | null;

    constructor(object?: any) {
        this.name = object.name;
        this.displayName = object.displayName;
        this.description = object.description;
        this.regex = object.regex;
        this.datatype = object.datatype;
        this.entitlementName = object.entitlementName;
        this.entitlementDisplayName = object.entitlementDisplayName;
        this.hasOverride = object.hasOverride === true;
        this.dropdownOptions = this.datatype === 'checkbox' ? null : parseRegexOptions(this.regex);

        const hasStoredValue = object.value !== undefined && object.value !== null;
        const rawValue = hasStoredValue ? object.value : deriveDefaultValue(this.regex);
        this.value = this.datatype === 'checkbox'
            ? (rawValue === true || rawValue === 'true')
            // Force lowercase regardless of the regex's own letter case (see parseRegexOptions)
            // and regardless of how a stored value happened to be cased when it was written.
            : String(rawValue).toLowerCase();
    }
}
